/* ═══════════════════════════════════════════════════════════
   WAND CUSTOM PRODUCT GALLERY — JS
   No Dawn dependencies. Vanilla JS only.

   Features:
   1. Thumbnail mouseenter (desktop) → hover preview of image.
   2. Thumbnail mouseleave from rail → revert to committed image.
   3. Thumbnail click → commit selection + update active border.
   4. Click on main image → open custom lightbox at active index.
   5. Lightbox: prev/next, ESC/backdrop/close-button to dismiss.
   6. Resize handler: re-sync frames when breakpoint changes.
   7. Mobile: infinite loop carousel via first/last slide clones — swiping
      past the last image continues to the first (and vice-versa) with no
      visible jump. Dots track the REAL images only. Desktop is unchanged.
═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // Product sections can load this asset again (quick add / theme editor).
  if (window.WandCustomGallery) {
    window.WandCustomGallery.initAll();
    return;
  }

  var DESKTOP_MIN = 990;

  function isDesktop() {
    return window.innerWidth >= DESKTOP_MIN;
  }


  /* ─── LIGHTBOX ────────────────────────────────────────────── */

  var lb = null;           // current lightbox DOM element
  var lbData = [];         // [{src, alt}] for all gallery images
  var lbIdx = 0;           // current lightbox image index
  var lbOwner = null;

  function lbOpen(images, startIndex) {
    if (lb) lbClose();    // close any existing lightbox first
    lbData = images;
    lbIdx = Math.max(0, Math.min(images.length - 1, startIndex));

    lb = document.createElement('div');
    lb.className = 'wand-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Image viewer');
    lb.innerHTML =
      '<div class="wand-lb__backdrop"></div>' +
      '<button class="wand-lb__close" type="button" aria-label="Close">&#x2715;</button>' +
      '<button class="wand-lb__prev" type="button" aria-label="Previous image">&#x2039;</button>' +
      '<div class="wand-lb__wrap"><img class="wand-lb__img" src="" alt="" /></div>' +
      '<button class="wand-lb__next" type="button" aria-label="Next image">&#x203A;</button>';

    document.body.appendChild(lb);
    document.body.classList.add('wand-lb-open');

    lbRender();

    lb.querySelector('.wand-lb__backdrop').addEventListener('click', lbClose);
    lb.querySelector('.wand-lb__close').addEventListener('click', lbClose);
    lb.querySelector('.wand-lb__prev').addEventListener('click', function () { lbNav(-1); });
    lb.querySelector('.wand-lb__next').addEventListener('click', function () { lbNav(1); });

    document.addEventListener('keydown', lbKeydown);
    lb.querySelector('.wand-lb__close').focus();
  }

  function lbRender() {
    if (!lb || !lbData.length) return;
    var d = lbData[lbIdx];
    var img = lb.querySelector('.wand-lb__img');
    img.src = d.src;
    img.alt = d.alt;
    lb.querySelector('.wand-lb__prev').hidden = lbIdx <= 0;
    lb.querySelector('.wand-lb__next').hidden = lbIdx >= lbData.length - 1;
  }

  function lbNav(dir) {
    lbIdx = Math.max(0, Math.min(lbData.length - 1, lbIdx + dir));
    lbRender();
  }

  function lbClose() {
    if (!lb) return;
    lb.remove();
    lb = null;
    lbOwner = null;
    document.body.classList.remove('wand-lb-open');
    document.removeEventListener('keydown', lbKeydown);
  }

  function lbKeydown(e) {
    if (e.key === 'Escape')      { lbClose(); }
    if (e.key === 'ArrowLeft')   { lbNav(-1); }
    if (e.key === 'ArrowRight')  { lbNav(1);  }
  }


  /* ─── GALLERY ─────────────────────────────────────────────── */

  function initGallery(gallery) {
    if (gallery.dataset.wandGalleryReady) return;

    var thumbsRail = gallery.querySelector('.wand-custom-gallery__thumbs');
    var thumbs = thumbsRail
      ? Array.prototype.slice.call(thumbsRail.querySelectorAll('.wand-custom-gallery__thumb'))
      : [];
    var frames = Array.prototype.slice.call(
      gallery.querySelectorAll('.wand-custom-gallery__frame')
    );
    var mainEl = gallery.querySelector('.wand-custom-gallery__main');

    if (!frames.length) return;
    gallery.dataset.wandGalleryReady = '1';

    var disposed = false;
    var removers = [];
    var pendingFrames = new Set();
    var unsubscribe = null;
    function listen(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      removers.push(function () { target.removeEventListener(name, handler, options); });
    }
    function nextFrame(callback) {
      var id = requestAnimationFrame(function () {
        pendingFrames.delete(id);
        if (!disposed) callback();
      });
      pendingFrames.add(id);
    }
    function destroy() {
      if (disposed) return;
      disposed = true;
      if (typeof unsubscribe === 'function') unsubscribe();
      removers.forEach(function (remove) { remove(); });
      pendingFrames.forEach(function (id) { cancelAnimationFrame(id); });
      clearTimeout(scrollTimer);
      clearTimeout(resizeTimer);
      teardownLoop();
      if (dotsWrap) dotsWrap.remove();
      if (lbOwner === gallery) lbClose();
      delete gallery.dataset.wandGalleryReady;
      delete gallery.wandGalleryDestroy;
      delete gallery.wandGalleryUpdate;
    }
    gallery.wandGalleryDestroy = destroy;

    // Build lightbox image array from frame data attributes
    var galleryImages = frames.map(function (f) {
      var img = f.querySelector('img');
      return {
        src: f.dataset.lightboxSrc || (img ? img.getAttribute('src') : ''),
        alt: f.dataset.lightboxAlt || (img ? img.getAttribute('alt') : '')
      };
    });

    // The frame Liquid marked is-active on load — matches the selected
    // variant's own image when the page loaded with ?variant=... (e.g. from
    // the Wand Grip color discovery page), or the first image otherwise
    // (see wand-custom-product-gallery.liquid). All initial JS state must
    // start here, not at 0, or a variant deep link would show the right
    // price/selected option but the wrong photo.
    var initialActiveIndex = 0;
    frames.forEach(function (f, i) {
      if (f.classList.contains('is-active')) {
        initialActiveIndex = i;
      }
    });

    // The "committed" index — set by click, reverted to on mouse leave
    var committedIndex = initialActiveIndex;

    // Show a frame by index.
    // commit=true  → also update thumbnail is-active border
    // commit=false → only swap the visible frame (hover preview)
    function showFrame(index, commit) {
      if (commit) {
        committedIndex = index;
        currentReal = index;
        thumbs.forEach(function (t) {
          var match = parseInt(t.dataset.index, 10) === index;
          t.classList.toggle('is-active', match);
          t.setAttribute('aria-pressed', match ? 'true' : 'false');
        });
      }

      // On desktop: toggle is-active class on frames.
      // On mobile:  scroll-snap drives visibility — no frame class change.
      if (isDesktop()) {
        frames.forEach(function (f) {
          var match = parseInt(f.dataset.index, 10) === index;
          f.classList.toggle('is-active', match);
          f.setAttribute('aria-hidden', match ? 'false' : 'true');
        });
      }
    }

    // ── Thumbnail events ──────────────────────────────────────

    thumbs.forEach(function (thumb) {
      // Desktop hover → preview (no commit, active border unchanged)
      listen(thumb, 'mouseenter', function () {
        if (!isDesktop()) return;
        showFrame(parseInt(thumb.dataset.index, 10), false);
      });

      // Click → commit (updates active border + frame)
      listen(thumb, 'click', function () {
        showFrame(parseInt(thumb.dataset.index, 10), true);
      });
    });

    // Rail mouseleave → revert to committed image
    if (thumbsRail) {
      listen(thumbsRail, 'mouseleave', function () {
        if (!isDesktop()) return;
        showFrame(committedIndex, false);
      });
    }

    // ── Lightbox ──────────────────────────────────────────────

    if (mainEl) {
      listen(mainEl, 'click', function () {
        var startIdx;
        if (isDesktop()) {
          startIdx = committedIndex;
        } else {
          // Mobile: real image index (clone-aware) from scroll position
          startIdx = realIndexNow();
        }
        lbOpen(galleryImages, startIdx);
        lbOwner = gallery;
      });
    }

    // ── Mobile slider dots ────────────────────────────────────
    // Built only when >1 image. Synced to the scroll-snap position.
    // Desktop hides them via CSS; the thumbnail rail drives nav there.

    var dotsWrap = null;

    // ── Mobile loop (infinite carousel) — state + helpers ─────────
    // We clone the first and last REAL slides (prepend last, append first).
    // When the user scrolls onto a clone we instantly jump to the matching
    // real slide; clone and real slide are pixel-identical, so it's invisible.
    // `frames` stays the REAL slides only, so dots / lightbox / desktop fade /
    // variant-sync are all unaffected by the clones.
    var n = frames.length;
    var loopEnabled = n > 1;
    var loopSetup = false;
    var loopRetries = 0;
    var cloneFirst = null;
    var cloneLast = null;
    var currentReal = initialActiveIndex;

    function syncDots(index) {
      currentReal = index;
      committedIndex = index;
      if (!dotsWrap) return;
      var dots = dotsWrap.children;
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === index);
      }
    }

    // Eagerly fetch the current slide's neighbours so a swipe reveals a
    // ready image instead of a brief blank frame (which reads as a slow /
    // delayed slide). The frame height is already reserved by aspect-ratio,
    // so this changes nothing about layout — it only warms the next image.
    // Purely additive: if a browser ignores the hint, lazy-loading still
    // applies and behaviour is unchanged.
    function preloadNeighbors(realIndex) {
      [realIndex - 1, realIndex, realIndex + 1].forEach(function (i) {
        var idx = ((i % n) + n) % n; // wrap (matches the loop carousel)
        var f = frames[idx];
        if (!f) return;
        var img = f.querySelector('img');
        if (img && !img.complete && img.getAttribute('loading') === 'lazy') {
          img.setAttribute('loading', 'eager');
        }
      });
    }

    // One slide's scroll distance = slide width + the flex gap between slides.
    function slideStep() {
      if (!mainEl) return 0;
      var w = mainEl.clientWidth;
      if (w <= 0) return 0;
      var gap = parseFloat(window.getComputedStyle(mainEl).columnGap || '0') || 0;
      return w + gap;
    }

    function rawIndexNow() {
      var step = slideStep();
      if (step <= 0) return 0;
      return Math.round(mainEl.scrollLeft / step);
    }

    // Map raw DOM slide index → real image index (0..n-1).
    // With clones: raw 0 = cloned last, 1..n = real, n+1 = cloned first.
    function realIndexNow() {
      var raw = rawIndexNow();
      if (!loopSetup) return Math.max(0, Math.min(n - 1, raw));
      return ((raw - 1) % n + n) % n;
    }

    function jumpToRaw(raw) {
      var step = slideStep();
      if (step > 0) mainEl.scrollLeft = raw * step;   // instant — no animation
    }

    function scrollToReal(realIndex, smooth) {
      var step = slideStep();
      if (step <= 0) return;
      var raw = loopSetup ? realIndex + 1 : realIndex;
      mainEl.scrollTo({ left: raw * step, behavior: smooth ? 'smooth' : 'auto' });
    }

    // Fired once scrolling settles: wrap from a clone back to its real slide.
    function handleSettle() {
      if (!mainEl || isDesktop()) return;
      if (loopSetup) {
        var raw = rawIndexNow();
        if (raw <= 0) jumpToRaw(n);              // on cloned-last  → real last
        else if (raw >= n + 1) jumpToRaw(1);     // on cloned-first → real first
      }
      var real = realIndexNow();
      syncDots(real);
      preloadNeighbors(real);
    }

    function setupLoop() {
      if (disposed || loopSetup || !loopEnabled || !mainEl || isDesktop()) return;
      var step = slideStep();
      if (step <= 0) {                            // layout not ready yet — retry
        if (loopRetries++ < 30) nextFrame(setupLoop);
        return;
      }
      loopRetries = 0;
      cloneLast = frames[n - 1].cloneNode(true);
      cloneFirst = frames[0].cloneNode(true);
      [cloneLast, cloneFirst].forEach(function (c) {
        c.classList.add('wand-custom-gallery__frame--clone');
        c.classList.remove('is-active');
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('id');
        c.removeAttribute('data-index');
        c.removeAttribute('data-media-id');
      });
      mainEl.insertBefore(cloneLast, mainEl.firstChild);
      mainEl.appendChild(cloneFirst);
      loopSetup = true;
      jumpToRaw(currentReal + 1);                 // show the current real slide
    }

    function teardownLoop() {
      if (!loopSetup) return;
      if (cloneFirst && cloneFirst.parentNode) cloneFirst.parentNode.removeChild(cloneFirst);
      if (cloneLast && cloneLast.parentNode) cloneLast.parentNode.removeChild(cloneLast);
      cloneFirst = cloneLast = null;
      loopSetup = false;
      mainEl.scrollLeft = currentReal * slideStep();  // keep current image in view
    }

    if (loopEnabled && mainEl) {
      dotsWrap = document.createElement('div');
      dotsWrap.className = 'wand-custom-gallery__dots';

      frames.forEach(function (f, i) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'wand-custom-gallery__dot' + (i === currentReal ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
        listen(dot, 'click', function () { scrollToReal(i, true); });
        dotsWrap.appendChild(dot);
      });

      mainEl.parentNode.insertBefore(dotsWrap, mainEl.nextSibling);

      // Build the loop clones (mobile only; safely a no-op on desktop).
      // Done before attaching the scroll listener so the initial reposition
      // does not trigger a spurious settle.
      setupLoop();

      // Warm the first swipe target up front so it never reveals blank.
      preloadNeighbors(currentReal);

      // Live dot sync (rAF-throttled) + wrap reset once scrolling settles.
      var rafPending = false;
      var scrollTimer;
      listen(mainEl, 'scroll', function () {
        if (isDesktop()) return;
        if (!rafPending) {
          rafPending = true;
          nextFrame(function () {
            rafPending = false;
            syncDots(realIndexNow());
          });
        }
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(handleSettle, 80);
      }, { passive: true });

      // scrollend (modern browsers) fires reliably after momentum ends; the
      // debounced fallback above covers older iOS. Both call the same
      // idempotent handler, so firing both is harmless.
      if ('onscrollend' in window) {
        listen(mainEl, 'scrollend', handleSettle);
      }
    }

    // ── Resize ────────────────────────────────────────────────

    var resizeTimer;
    var resizeRafPending = false;
    listen(window, 'resize', function () {
      if (!gallery.isConnected) { destroy(); return; }
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (resizeRafPending) return;
        resizeRafPending = true;
        nextFrame(function () {
          resizeRafPending = false;
          showFrame(committedIndex, true);
          if (!loopEnabled || !mainEl) return;
          if (isDesktop()) {
            teardownLoop();                  // desktop fade takes over — no clones
          } else if (!loopSetup) {
            setupLoop();                     // crossed into mobile → build the loop
          } else {
            jumpToRaw(currentReal + 1);      // re-align to current slide at new width
            syncDots(currentReal);
          }
        });
      }, 120);
    });

    // Reuse the response already fetched by the owning product-info. Scoping
    // this to the instance prevents main PDP / quick-add cross-talk.
    function updateMedia(html, variant) {
      if (disposed) return;
      if (!gallery.isConnected) { destroy(); return; }
      var owner = gallery.closest('product-info');
      var sourceSection = owner
        ? owner.dataset.originalSection || owner.dataset.section
        : gallery.id.replace('WandGallery-', '');
      var incoming = html && html.getElementById('WandGallery-' + sourceSection);
      if (incoming && incoming.dataset.mediaKey) {
        if (incoming.dataset.selectedVariant && incoming.dataset.selectedVariant !== String(variant && variant.id)) return;
        if (incoming.dataset.mediaKey !== gallery.dataset.mediaKey) {
          var replacement = document.importNode(incoming, true);
          // The server renders the original section ID; quick add owns a
          // distinct local ID. Preserve that identity across gallery swaps.
          replacement.id = gallery.id;
          replacement.dataset.galleryId = gallery.dataset.galleryId;
          var replacementMain = replacement.querySelector('.wand-custom-gallery__main');
          if (replacementMain && mainEl) replacementMain.id = mainEl.id;
          destroy();
          gallery.replaceWith(replacement);
          initGallery(replacement);
          return;
        }
        gallery.dataset.selectedVariant = variant ? String(variant.id) : '';
        // Same group, different real variant: keep the shopper's slide.
        if (gallery.dataset.mockupGroup) return;
      }
      if (!variant || !variant.featured_media) return;

      var mediaId = String(variant.featured_media.id);

      // Find the matching frame by data-media-id
      var matchIndex = -1;
      frames.forEach(function (f, i) {
        if (String(f.dataset.mediaId) === mediaId) matchIndex = i;
      });

      if (matchIndex < 0) return; // Variant image not in gallery — no change

      // Commit the new frame (updates gallery + thumbnail border)
      showFrame(matchIndex, true);

      // Mobile: scroll the snap container to the correct (real) slide
      if (!isDesktop() && mainEl) {
        scrollToReal(matchIndex, true);
        syncDots(matchIndex);
      }
    }
    gallery.wandGalleryUpdate = updateMedia;

    // Legacy standalone galleries still support Dawn's native image event.
    // product-info instances use the scoped method above, not the global bus.
    if (!gallery.closest('product-info') && typeof subscribe === 'function') {
      unsubscribe = subscribe('variant-change', function (event) {
        var data = event && event.data;
        if (data && data.sectionId === gallery.id.replace('WandGallery-', '')) {
          updateMedia(data.html, data.variant);
        }
      });
    }
  }

  function initAll() {
    var galleries = document.querySelectorAll('.wand-custom-gallery');
    Array.prototype.forEach.call(galleries, initGallery);
  }

  window.WandCustomGallery = { initAll: initAll };
  if (!customElements.get('wand-custom-gallery')) {
    customElements.define('wand-custom-gallery', class extends HTMLElement {
      connectedCallback() { initGallery(this); }
      disconnectedCallback() { if (this.wandGalleryDestroy) this.wandGalleryDestroy(); }
      updateMedia(html, variant) { if (this.wandGalleryUpdate) this.wandGalleryUpdate(html, variant); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();

